import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import SoftBox from "../../../components/SoftBox";
import Breadcrumbs from "../../Breadcrumbs";
import {navbar, navbarContainer, navbarMobileMenu, navbarRow,} from "./styles";
import {
  setAuthentication,
  setLoggedInUserDetail,
  setMiniSidenav,
  setTransparentNavbar,
  useRayzayUIController
} from "../../../context";
import {clearAllAuthData, logout} from "../../../api/auth/authApiClient";
import Avatar from "@mui/material/Avatar";
import ProfileDropdown from "../../ProfileDropDown";
import NotificationBell from "../../NotificationBell";

function DashboardNavbar({absolute = false, light = false, isMini = false}) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useRayzayUIController();
  const { isAuthenticated } = controller;
  const {loggedInUserDetail} = controller;
  const {miniSidenav, transparentNavbar, fixedNavbar} = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleProfileClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const openProfileMenu = Boolean(anchorEl);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleEditClose = () => setAnchorEl(null);


  const handleLogOut = async () => {
    try {
      await logout(); // This will clear localStorage tokens

      // Clear authentication state from context
      setAuthentication(dispatch, false);
      setLoggedInUserDetail(dispatch, null);

      // Force redirect to login page
      window.location.href = '/authentication/sign-in';
    } catch (error) {
      console.error('Logout failed:', error);

      // Even if logout API fails, clear local state
      setAuthentication(dispatch, false);
      setLoggedInUserDetail(dispatch, null);

      // Clear all auth data manually
      clearAllAuthData();

      // Force redirect to login page
      window.location.href = '/authentication/sign-in';
    }
    handleEditClose();
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              <SoftBox
                  color={light ? "white" : "inherit"}
                  sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: {xs: 1, sm: 2},
                      justifyContent: 'flex-end',
                      width: '100%'
                  }}
              >
              <IconButton
                size="small"
                color="inherit"
                sx={{
                    ...navbarMobileMenu,
                    minWidth: {xs: 44, sm: 40},
                    minHeight: {xs: 44, sm: 40}
                }}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
                  {isAuthenticated && <NotificationBell light={light}/>}
                  <IconButton
                      onClick={handleProfileClick}
                      size="small"
                      sx={{
                          p: 0.5,
                          minWidth: {xs: 44, sm: 40},
                          minHeight: {xs: 44, sm: 40},
                          border: openProfileMenu ? 2 : 0,
                          borderColor: openProfileMenu ? 'primary.main' : 'transparent',
                          transition: 'all 0.2s ease'
                      }}
                  >
                <Avatar
                    src={loggedInUserDetail?.profilePic ? `data:image/png;base64,${loggedInUserDetail.profilePic}` : undefined}
                    alt={loggedInUserDetail?.firstName || "User"}
                    sx={{
                        width: {xs: 36, sm: 40},
                        height: {xs: 36, sm: 40},
                        fontSize: {xs: '1rem', sm: '1.25rem'},
                        bgcolor: !loggedInUserDetail?.profilePic ? 'primary.main' : 'transparent'
                    }}
                >
                    {!loggedInUserDetail?.profilePic && (
                        (loggedInUserDetail?.firstName?.[0] || 'U').toUpperCase()
                    )}
                </Avatar>
              </IconButton>
              {isAuthenticated ? (
                  <ProfileDropdown
                      openProfileMenu={openProfileMenu}
                      anchorEl={anchorEl}
                      setAnchorEl={setAnchorEl}
                      loggedInUserDetail={loggedInUserDetail}
                      handleLogOut={handleLogOut}
                  />
              ) : null}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}


// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
